import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Button,
  Link,
  Alert,
} from '@mui/material';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'react-toastify';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import AuthPageShell from '@components/layout/AuthPageShell';
import AuthFormCard, { authLinkSx, authPrimaryCtaSx } from '@components/layout/AuthFormCard';
import { brandColors } from '@config/brandTokens';

const resetPasswordSchema = z.object({
  password: z.string().min(6, 'Password must be at least 6 characters'),
  confirmPassword: z.string().min(6, 'Please confirm your password'),
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords don't match",
  path: ['confirmPassword'],
});

type ResetPasswordFormData = z.infer<typeof resetPasswordSchema>;

const ResetPasswordPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ResetPasswordFormData>({
    resolver: zodResolver(resetPasswordSchema),
  });

  useEffect(() => {
    if (!token) {
      setError('Invalid or missing reset token. Please request a new password reset link.');
    }
  }, [token]);

  const onSubmit = async (data: ResetPasswordFormData) => {
    if (!token) {
      setError('Invalid reset token');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const response = await fetch('/api/auth/reset-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, newPassword: data.password }),
      });

      const result = await response.json();

      if (result.success) {
        setSuccess(true);
        toast.success(result.message || 'Password has been reset successfully');
        setTimeout(() => {
          navigate('/login');
        }, 2000);
      } else {
        setError(result.message || 'Failed to reset password');
        toast.error(result.message || 'Failed to reset password');
      }
    } catch (error: any) {
      console.error('Reset password error:', error);
      setError('An error occurred. Please try again.');
      toast.error('An error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  if (success) {
    return (
      <AuthPageShell>
        <AuthFormCard roleLabel="Account" title="Password reset successful" chrome backTo="/login">
          <Alert severity="success" sx={{ mb: 3 }}>
            Your password has been reset successfully. Redirecting to login...
          </Alert>
          <Button fullWidth variant="contained" onClick={() => navigate('/login')} sx={authPrimaryCtaSx}>
            Go to login
          </Button>
        </AuthFormCard>
      </AuthPageShell>
    );
  }

  return (
    <AuthPageShell>
      <AuthFormCard
        roleLabel="Account"
        title="Reset password"
        subtitle="Enter your new password below."
        chrome
        backTo="/login"
      >
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {!token ? (
            <Box>
              <Alert severity="error" sx={{ mb: 3 }}>
                Invalid or missing reset token. Please request a new password reset link.
              </Alert>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate('/forgot-password')}
                sx={{ textTransform: 'none', fontWeight: 700 }}
              >
                Request new reset link
              </Button>
            </Box>
          ) : (
            <Box component="form" onSubmit={handleSubmit(onSubmit)}>
              <TextField
                {...register('password')}
                label="New Password"
                type="password"
                fullWidth
                margin="normal"
                autoComplete="new-password"
                autoFocus
                error={!!errors.password}
                helperText={errors.password?.message}
              />

              <TextField
                {...register('confirmPassword')}
                label="Confirm New Password"
                type="password"
                fullWidth
                margin="normal"
                autoComplete="new-password"
                error={!!errors.confirmPassword}
                helperText={errors.confirmPassword?.message}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                sx={{ mt: 3, mb: 2, ...authPrimaryCtaSx }}
                disabled={isLoading}
              >
                {isLoading ? 'Resetting...' : 'Reset password'}
              </Button>

              <Box sx={{ mt: 1, textAlign: 'center', ...authLinkSx }}>
                <Link component={RouterLink} to="/login" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
                  <ArrowBackIcon fontSize="small" />
                  <Typography variant="body2" sx={{ color: brandColors.blue }}>
                    Back to login
                  </Typography>
                </Link>
              </Box>
            </Box>
          )}
      </AuthFormCard>
    </AuthPageShell>
  );
};

export default ResetPasswordPage;
